import { apiClient } from "./client";
import type { AccreditoEffettivo } from "./clients";

// ---------------------------------------------------------------------------
// «Utenti autorizzati e conti» di una sede (routes/sedeUtentiConti.ts):
// utenze delle casseforti, conto tecnico per cassaforte, conto di accredito e
// avviso «una sede, un conto». Chi non può modificare la sede riceve IBAN e
// conti già mascherati dal server (ultime 4 cifre).
// ---------------------------------------------------------------------------

/** Rispecchia l'enum Prisma `TipoUtenteCassaforte`. */
export type TipoUtenteCassaforte = "super_user" | "user" | "service" | "cit" | "super_user_tecnico";
/** Rispecchia l'enum Prisma `OrigineDatoSede`. */
export type OrigineDato = "aznext" | "excel" | "manuale";

/** Stesso ordine ed etichette del backend (`utils/utentiCassaforte.ts`) e del foglio Excel. */
export const ORDINE_TIPI: TipoUtenteCassaforte[] = ["super_user", "user", "service", "cit", "super_user_tecnico"];
export const ETICHETTE_TIPO: Record<TipoUtenteCassaforte, string> = {
  super_user: "Super User",
  user: "User",
  service: "Service",
  cit: "Cit",
  super_user_tecnico: "Super User tecnico",
};

export interface UtenteCassaforte {
  id: string;
  tipo: TipoUtenteCassaforte;
  username: string;
  nominativo: string | null;
  origine: OrigineDato;
  /** Il conto su cui AZNext accredita questa utenza: lo scrive solo l'import. */
  conto_tecnico_aznext: string | null;
  visto_in_aznext_il: string | null;
  updated_at: string;
  device: { id: string; matricola: string | null } | null;
}

export interface CassaforteConConto {
  device_cliente_id: string;
  device_id: string;
  matricola: string;
  conto_tecnico: string | null;
  /** null = scritto prima che l'origine si registrasse (vale come scritto da una persona). */
  conto_tecnico_origine: OrigineDato | null;
  conto_tecnico_aggiornato_il: string | null;
}

export interface UtentiContiSede {
  can_edit: boolean;
  /** `can_edit` più il permesso di modifica dei dispositivi: il conto tecnico sta sulla relazione cassaforte–cliente. */
  can_edit_conto: boolean;
  utenti: UtenteCassaforte[];
  casseforti: CassaforteConConto[];
  accredito: AccreditoEffettivo & { origine: OrigineDato | null; aggiornato_il: string | null };
  avvisi: {
    conti_condivisi: {
      conto: string;
      altre_sedi: { id: string; nome_sede: string | null; cliente_id: string | null; cliente_nome: string | null }[];
    }[];
    piu_conti: string[];
  };
}

/** Il riepilogo che `GET /clienti/:id/sedi` aggiunge a ogni sede, per colonne e filtri dell'elenco. */
export interface RiepilogoUtentiConti {
  utenze: number;
  casseforti: number;
  casseforti_con_conto: number;
  accredito: "iban" | "conto_interno" | "ereditato" | null;
  conto_condiviso: boolean;
  piu_conti: boolean;
}

export interface DatiUtenza {
  tipo: TipoUtenteCassaforte;
  username: string;
  nominativo: string | null;
  device_id: string | null;
}

export type DatiAccredito =
  | { tipo: "iban"; iban: string; beneficiario: string | null; banca: string | null; agenzia: string | null }
  | { tipo: "conto_interno"; conto_interno: string; beneficiario: string | null; banca: string | null; agenzia: string | null }
  | { tipo: null };

export async function getUtentiContiSede(sedeId: string): Promise<UtentiContiSede> {
  const { data } = await apiClient.get<UtentiContiSede>(`/sedi/${sedeId}/utenti-conti`);
  return data;
}

export async function createUtenza(sedeId: string, dati: DatiUtenza): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>("/utenti-cassaforte", { sede_id: sedeId, ...dati });
  return data;
}

export async function updateUtenza(id: string, dati: DatiUtenza): Promise<{ id: string }> {
  const { data } = await apiClient.patch<{ id: string }>(`/utenti-cassaforte/${id}`, dati);
  return data;
}

export async function deleteUtenza(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/utenti-cassaforte/${id}`);
  return data;
}

export async function salvaContoTecnico(sedeId: string, deviceClienteId: string, conto: string | null): Promise<{ cambiato: boolean }> {
  const { data } = await apiClient.patch<{ cambiato: boolean }>(`/sedi/${sedeId}/conto-tecnico`, {
    device_cliente_id: deviceClienteId,
    conto_tecnico: conto,
  });
  return data;
}

export async function salvaAccredito(sedeId: string, dati: DatiAccredito): Promise<{ cambiato: boolean }> {
  const { data } = await apiClient.patch<{ cambiato: boolean }>(`/sedi/${sedeId}/accredito`, dati);
  return data;
}

// ---------------------------------------------------------------------------
// Excel «elenco utenti» (routes/utentiExcel.ts): un foglio per sede.
// Dalla sede un foglio, dal cliente uno per sede. Upload in due passi:
// anteprima (non scrive) e applica (rimanda lo stesso file con l'impronta
// dell'anteprima e le utenze assenti da togliere).
// ---------------------------------------------------------------------------

export type AmbitoExcel = "sede" | "cliente";

export interface AnteprimaFoglioExcel {
  sede_id: string;
  nome_sede: string;
  foglio: string;
  nuove: { riga: number; tipo: TipoUtenteCassaforte; username: string; nominativo: string | null; riapre_tolta_a_mano: boolean }[];
  modificate: { riga: number; id: string; tipo: TipoUtenteCassaforte; username: string; prima: string | null; dopo: string }[];
  invariate: { riga: number; id: string; tipo: TipoUtenteCassaforte; username: string }[];
  conflitti: { riga: number | null; username: string | null; motivo: string }[];
  assenti: { id: string; tipo: TipoUtenteCassaforte; username: string; nominativo: string | null; device_id: string | null; origine: OrigineDato }[];
  conti_tecnici: { device_cliente_id: string; matricola: string; prima: string | null; dopo: string }[];
  accredito: { prima: { tipo: string | null; valore: string | null }; dopo: { tipo: "iban" | "conto_interno"; valore: string } } | null;
}

export interface AnteprimaExcel {
  file: string;
  fogli: AnteprimaFoglioExcel[];
  /** Errori che impediscono di applicare il file: niente si scrive. */
  errori: string[];
  /** null quando ci sono errori. */
  impronta: string | null;
}

export interface EsitoExcel {
  sede_id: string;
  nome_sede: string;
  nuove: number;
  modificate: number;
  rimosse: number;
  conti_tecnici: number;
  accredito: boolean;
}

/**
 * Il nome lo decide il server (`Content-Disposition`); il blob passa da axios per il token.
 * Restituisce quante sedi del cliente non sono nel file perché senza casseforti.
 */
export async function scaricaExcelUtenti(ambito: AmbitoExcel, id: string): Promise<{ senza_casseforti: number }> {
  let res;
  try {
    res = await apiClient.get<Blob>(`/utenti-excel/${ambito}/${id}`, { responseType: "blob" });
  } catch (err) {
    // Con `responseType: "blob"` anche l'errore arriva come Blob: se ne legge il
    // messaggio JSON, così a schermo va il testo del backend.
    const dati = (err as { response?: { data?: unknown } }).response?.data;
    if (dati instanceof Blob) {
      const testo = await dati.text().catch(() => "");
      let messaggio: string | undefined;
      try {
        messaggio = (JSON.parse(testo) as { error?: string }).error;
      } catch {
        messaggio = undefined;
      }
      if (messaggio) throw new Error(messaggio);
    }
    throw err;
  }
  const disposizione = String(res.headers["content-disposition"] ?? "");
  const nome = /filename="([^"]+)"/.exec(disposizione)?.[1] ?? "elenco_utenti.xlsx";
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return { senza_casseforti: Number(res.headers["x-sedi-senza-casseforti"] ?? 0) || 0 };
}

export async function anteprimaExcelUtenti(ambito: AmbitoExcel, id: string, file: File): Promise<AnteprimaExcel> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await apiClient.post<AnteprimaExcel>(`/utenti-excel/${ambito}/${id}/anteprima`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function applicaExcelUtenti(
  ambito: AmbitoExcel,
  id: string,
  file: File,
  impronta: string,
  rimuovi: string[]
): Promise<{ esiti: EsitoExcel[] }> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("impronta", impronta);
  formData.append("rimuovi", JSON.stringify(rimuovi));
  const { data } = await apiClient.post<{ esiti: EsitoExcel[] }>(`/utenti-excel/${ambito}/${id}/applica`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
